import React, { Component } from 'react';

import '../../style/ProjectStats.scss';
import { backEndUri, apolloClient } from '../../config';
import queries from '../../queries';
import { t, dateLocale } from '../../i18n';
import { exportStatsPptx } from './statsPptx';
import { identityProvider, isUserAdmin } from '../../utils';
import { showToast } from '../../toast';
import confirmDialog from '../dialogs/ConfirmDialog';

const COLORS = ['#5b5ce6', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#ec4899', '#84cc16'];
const PERIODS = [7, 30, 90, 0];
const nf = (n) => new Intl.NumberFormat().format(n || 0);
const pct = (part, total) => (total > 0 ? `${Math.round((part / total) * 1000) / 10} %` : '—');

// Camembert (donut) : une part par page, la couleur sert aussi de légende dans le tableau.
function Donut({ slices, total }) {
  const R = 70; const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <svg viewBox='0 0 200 200' className='tsk-pstats__donut' role='img' aria-label={t('ui.stats.share_of_clicks')}>
      <circle cx='100' cy='100' r={R} fill='none' stroke='currentColor' strokeOpacity='0.08' strokeWidth='34' />
      {total > 0 && slices.map((s) => {
        const len = (s.value / total) * C;
        const el = (
          <circle key={s.label} cx='100' cy='100' r={R} fill='none' stroke={s.color} strokeWidth='34'
            strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} transform='rotate(-90 100 100)'>
            <title>{`${s.label} — ${nf(s.value)} (${pct(s.value, total)})`}</title>
          </circle>
        );
        offset += len;
        return el;
      })}
      <text x='100' y='96' textAnchor='middle' className='tsk-pstats__donut-num'>{nf(total)}</text>
      <text x='100' y='118' textAnchor='middle' className='tsk-pstats__donut-label'>{t('ui.stats.clicks')}</text>
    </svg>
  );
}

// Histogramme par jour : barres (vues en gris, clics en couleur), dates sur l'axe, pic et valeurs au survol.
const fmtDay = (day, opts = { day: 'numeric', month: 'short' }) => new Date(`${day}T12:00:00`).toLocaleDateString(dateLocale, opts);

function Timeline({ days }) {
  if (!days.length) return <p className='tsk-pstats__none'>{t('ui.stats.no_data')}</p>;
  const max = Math.max(1, ...days.map((d) => Math.max(d.clicks, d.views)));
  const n = days.length;
  const step = Math.max(1, Math.ceil(n / 7));
  const ticks = days.map((d, i) => i).filter((i) => i % step === 0 || i === n - 1).filter((i, k, arr) => !(i === n - 1 && k > 0 && i - arr[k - 1] < step / 2));
  const peak = days.reduce((a, b) => (b.clicks > a.clicks ? b : a), days[0]);
  return (
    <div className='tsk-pstats__tl'>
      {peak.clicks > 0 && <p className='tsk-pstats__tl-peak'>{t('ui.stats.insight_peak', { day: fmtDay(peak.day, { weekday: 'long', day: 'numeric', month: 'long' }), n: nf(peak.clicks) })}</p>}
      <div className='tsk-pstats__tl-chart'>
        <div className='tsk-pstats__tl-y'><span>{nf(max)}</span><span>{nf(Math.round(max / 2))}</span><span>0</span></div>
        <div className='tsk-pstats__tl-plot'>
          <div className='tsk-pstats__tl-bars'>
            {days.map((d) => (
              <div key={d.day} className='tsk-pstats__tl-col' title={`${fmtDay(d.day, { weekday: 'long', day: 'numeric', month: 'long' })} — ${nf(d.clicks)} ${t('ui.stats.clicks')}, ${nf(d.views)} ${t('ui.stats.views')}`}>
                <i className='tsk-pstats__bar-views' style={{ height: `${(d.views / max) * 100}%` }} />
                <i className='tsk-pstats__bar-clicks' style={{ height: `${(d.clicks / max) * 100}%` }} />
              </div>
            ))}
          </div>
          <div className='tsk-pstats__tl-x'>
            {ticks.map((i) => <span key={days[i].day} style={{ left: `${((i + 0.5) / n) * 100}%` }}>{fmtDay(days[i].day)}</span>)}
          </div>
        </div>
      </div>
    </div>
  );
}

function HBars({ rows, labelKey, valueKey, colorFn }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <ul className='tsk-pstats__hbars'>
      {rows.map((r, i) => (
        <li key={`${r[labelKey]}-${i}`}>
          <span className='tsk-pstats__hbars-label' title={r[labelKey]}>{r[labelKey]}</span>
          <span className='tsk-pstats__hbars-track'>
            <span style={{ width: `${(r[valueKey] / max) * 100}%`, background: colorFn ? colorFn(i) : COLORS[0] }} />
          </span>
          <span className='tsk-pstats__hbars-value'>{nf(r[valueKey])}</span>
        </li>
      ))}
    </ul>
  );
}

// Page « Statistiques » d'un projet : remplace le simple téléchargement du CSV (toujours disponible en bouton).
export default class ProjectStats extends Component {
  state = { days: 30, data: null, error: false, loading: true, resetBefore: '', exporting: false };

  componentDidMount() { this.load(); }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.projectId !== this.props.match.params.projectId) this.load();
  }

  async load(days = this.state.days) {
    const { projectId } = this.props.match.params;
    this.setState({ days, loading: true, error: false });
    try {
      let token;
      try { token = await identityProvider.currentUser().jwt(); } catch (e) { /* sans jeton */ }
      const response = await fetch(`${backEndUri}/project/${projectId}/stats/summary?days=${days}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(String(response.status));
      this.setState({ data: await response.json(), loading: false });
    } catch (e) {
      this.setState({ error: true, loading: false });
    }
  }


  // Présentation PowerPoint reprenant toutes les statistiques affichées.
  async exportPptx() {
    const { projectId } = this.props.match.params;
    const { data, days } = this.state;
    if (!data) return;
    this.setState({ exporting: true });
    try {
      let projectName = projectId;
      try {
        const res = await apolloClient.query({ query: queries.projects.get, variables: { id: projectId } });
        projectName = res.data.project.name || projectId;
      } catch (e) { /* le nom reste l'identifiant */ }
      await exportStatsPptx({ projectName, periodLabel: days === 0 ? t('ui.stats.all_time') : t('ui.stats.last_days', { n: days }), data });
    } catch (e) {
      showToast(t('ui.stats.pptx_error'), 'error', 6000);
    }
    this.setState({ exporting: false });
  }

  // Remise à zéro des clics et vues (jamais les e-mails, formulaires ni scores). before = 'YYYY-MM-DD' ou '' pour tout.
  async reset(before) {
    const { projectId } = this.props.match.params;
    const qs = before ? `before=${before}&` : '';
    let token;
    try { token = await identityProvider.currentUser().jwt(); } catch (e) { /* sans jeton */ }
    const call = (extra) => fetch(`${backEndUri}/project/${projectId}/stats?${qs}${extra}`, {
      method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    try {
      const preview = await call('dryRun=1');
      if (!preview.ok) throw new Error(String(preview.status));
      const { count } = await preview.json();
      if (!count) { showToast(t('ui.stats.reset_nothing'), 'info'); return; }
      await confirmDialog(
        <div>
          <p>{before ? t('ui.stats.reset_confirm_before', { n: nf(count), date: before }) : t('ui.stats.reset_confirm_all', { n: nf(count) })}</p>
          <p>{t('ui.stats.reset_keep')}</p>
        </div>,
        { confirmLabel: t('ui.stats.reset_button') }
      );
      const done = await call('');
      if (!done.ok) throw new Error(String(done.status));
      showToast(t('ui.stats.reset_done'));
      this.load();
    } catch (e) {
      if (e && e.message && /^\d+$/.test(e.message)) showToast(t('ui.stats.reset_error'), 'error', 6000);
    }
  }

  render() {
    const { projectId } = this.props.match.params;
    const { days, data, error, loading } = this.state;
    const totals = data ? data.totals : null;
    const UNKNOWN = t('ui.stats.unknown_page');
    const pages = data ? data.pages.filter((p) => p.clicks > 0 || p.views > 0).map((p) => (p.page === '—' ? { ...p, page: UNKNOWN, unknown: true } : p)) : [];
    const clickPages = pages.filter((p) => p.clicks > 0);
    const slices = clickPages.slice(0, 7).map((p, i) => ({ label: p.page, value: p.clicks, color: COLORS[i] }));
    const rest = clickPages.slice(7).reduce((sum, p) => sum + p.clicks, 0);
    if (rest > 0) slices.push({ label: t('ui.stats.others'), value: rest, color: '#94a3b8' });
    const named = clickPages.filter((p) => !p.unknown);
    const top = named[0];
    const byRate = pages.filter((p) => !p.unknown && p.views >= 5).map((p) => ({ ...p, rate: p.clicks / p.views })).sort((a, b) => b.rate - a.rate)[0];
    const quiet = pages.filter((p) => !p.unknown && p.views >= 5).sort((a, b) => a.clicks / a.views - b.clicks / b.views)[0];

    return (
      <div className='tsk-page-board tsk-pstats'>
        <header className='tsk-page-hero'>
          <h1 className='tsk-page-hero__title'>{t('ui.stats.title')}</h1>
          <p className='tsk-page-hero__subtitle'>{t('ui.stats.sub')}</p>
        </header>

        <div className='tsk-pstats__toolbar'>
          <div className='tsk-pstats__periods'>
            {PERIODS.map((p) => (
              <button key={p} type='button' className={p === days ? 'is-active' : ''} onClick={() => this.load(p)}>
                {p === 0 ? t('ui.stats.all_time') : t('ui.stats.last_days', { n: p })}
              </button>
            ))}
          </div>
          <div className='tsk-pstats__exports'>
            <button type='button' className='tsk-pstats__pptx' disabled={!data || loading || this.state.exporting} onClick={() => this.exportPptx()}>{this.state.exporting ? t('ui.stats.pptx_busy') : t('ui.stats.pptx')}</button>
            <a className='tsk-pstats__csv' href={`${backEndUri}/project/${projectId}/stats`} download>{t('ui.stats.csv')}</a>
          </div>
        </div>

        {loading && !data && <p>{t('ui.common.loading')}</p>}
        {error && <p>{t('ui.stats.error')}</p>}

        {data && (
          <div style={{ opacity: loading ? 0.5 : 1 }}>
            <div className='tsk-pstats__kpis'>
              <div><strong>{nf(totals.clicks)}</strong><span>{t('ui.stats.clicks')}</span></div>
              <div><strong>{nf(totals.views)}</strong><span>{t('ui.stats.views')}</span></div>
              <div><strong>{totals.sessions > 0 ? (Math.round((totals.clicks / totals.sessions) * 10) / 10) : '—'}</strong><span>{t('ui.stats.clicks_per_session')}</span></div>
              <div><strong>{nf(totals.sessions)}</strong><span>{t('ui.stats.sessions')}</span></div>
              <div><strong>{nf(totals.devices)}</strong><span>{t('ui.stats.screens')}</span></div>
            </div>

            {pages.length === 0 ? (
              <div className='tsk-empty'><h3>{t('ui.stats.no_data')}</h3><p>{t('ui.stats.no_data_text')}</p></div>
            ) : (
              <>
                {(top || byRate || quiet) && (
                  <ul className='tsk-pstats__insights'>
                    {top && <li>{t('ui.stats.insight_top', { page: top.page, share: pct(top.clicks, totals.clicks) })}</li>}
                    {byRate && <li>{t('ui.stats.insight_rate', { page: byRate.page, rate: pct(byRate.clicks, byRate.views) })}</li>}
                    {quiet && quiet.page !== (byRate && byRate.page) && <li>{t('ui.stats.insight_quiet', { page: quiet.page, rate: pct(quiet.clicks, quiet.views) })}</li>}
                  </ul>
                )}

                <div className='tsk-pstats__grid'>
                  <section className='tsk-pstats__card'>
                    <h2>{t('ui.stats.share_of_clicks')}</h2>
                    <div className='tsk-pstats__donut-wrap'>
                      <Donut slices={slices} total={slices.reduce((s, x) => s + x.value, 0)} />
                      <ul className='tsk-pstats__legend'>
                        {slices.map((s) => (
                          <li key={s.label}><i style={{ background: s.color }} /><span>{s.label}</span><b>{pct(s.value, totals.clicks)}</b></li>
                        ))}
                      </ul>
                    </div>
                  </section>

                  <section className='tsk-pstats__card'>
                    <h2>{t('ui.stats.compare_pages')}</h2>
                    <HBars rows={clickPages.slice(0, 10)} labelKey='page' valueKey='clicks' colorFn={(i) => COLORS[i] || '#94a3b8'} />
                  </section>

                  <section className='tsk-pstats__card tsk-pstats__card--wide'>
                    <h2>{t('ui.stats.per_day')}</h2>
                    <Timeline days={data.days} />
                    <p className='tsk-pstats__hint'><i className='tsk-pstats__sw tsk-pstats__sw--views' />{t('ui.stats.views')} <i className='tsk-pstats__sw tsk-pstats__sw--clicks' />{t('ui.stats.clicks')}</p>
                  </section>

                  <section className='tsk-pstats__card tsk-pstats__card--wide'>
                    <h2>{t('ui.stats.table')}</h2>
                    <table className='tsk-pstats__table'>
                      <thead><tr><th>{t('ui.stats.page')}</th><th>{t('ui.stats.views')}</th><th>{t('ui.stats.clicks')}</th><th>{t('ui.stats.click_rate')}</th><th>{t('ui.stats.share')}</th></tr></thead>
                      <tbody>
                        {pages.map((p) => (
                          <tr key={p.page}><td>{p.page}</td><td>{nf(p.views)}</td><td>{nf(p.clicks)}</td><td>{pct(p.clicks, p.views)}</td><td>{pct(p.clicks, totals.clicks)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </section>

                  {data.devices.length > 1 && (
                    <section className='tsk-pstats__card'>
                      <h2>{t('ui.stats.compare_screens')}</h2>
                      <HBars rows={data.devices.slice(0, 8)} labelKey='device' valueKey='clicks' />
                    </section>
                  )}

                  {data.flows.length > 0 && (
                    <section className='tsk-pstats__card'>
                      <h2>{t('ui.stats.flows')}</h2>
                      <HBars rows={data.flows.slice(0, 8).map((f) => ({ label: `${f.from_page} → ${f.to_page}`, count: f.count }))} labelKey='label' valueKey='count' />
                    </section>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {isUserAdmin(identityProvider.currentUser()) && (
          <details className='tsk-pstats__reset'>
            <summary>{t('ui.stats.reset_title')}</summary>
            <p>{t('ui.stats.reset_help')}</p>
            <div className='tsk-pstats__reset-row'>
              <label>{t('ui.stats.reset_before_label')}
                <input type='date' value={this.state.resetBefore} onChange={(e) => this.setState({ resetBefore: e.target.value })} />
              </label>
              <button type='button' disabled={!this.state.resetBefore} onClick={() => this.reset(this.state.resetBefore)}>{t('ui.stats.reset_before_button')}</button>
              <button type='button' className='is-danger' onClick={() => this.reset('')}>{t('ui.stats.reset_all_button')}</button>
            </div>
          </details>
        )}
      </div>
    );
  }
}
